import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CalendarDays } from "lucide-react";
import { RefObject, useEffect, useRef, useState } from "react";
import { cn } from "./ui";

type Props = { target: RefObject<HTMLDivElement>; onToday?: () => void; step?: number };

export function HScrollBar({ target, onToday, step = 280 }: Props) {
  const bar = useRef<HTMLDivElement>(null);
  const [w, setW] = useState({ scroll: 0, client: 0, left: 0 });
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    let lock = false;
    const sync = () => { if (lock) return; lock = true; setW({ scroll: el.scrollWidth, client: el.clientWidth, left: el.scrollLeft }); if (bar.current) bar.current.scrollLeft = el.scrollLeft; lock = false; };
    sync();
    el.addEventListener("scroll", sync);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    const mo = new MutationObserver(sync);
    mo.observe(el, { childList: true, subtree: true });
    return () => { el.removeEventListener("scroll", sync); ro.disconnect(); mo.disconnect(); };
  }, [target]);
  const go = (dx: number | "start" | "end") => {
    const el = target.current;
    if (!el) return;
    el.scrollTo({ left: dx === "start" ? 0 : dx === "end" ? el.scrollWidth : el.scrollLeft + dx, behavior: "smooth" });
  };
  const atStart = w.left <= 2, atEnd = w.left + w.client >= w.scroll - 2;
  const pct = w.scroll > w.client ? Math.round((w.left / (w.scroll - w.client)) * 100) : 100;
  return (
    <div className="sticky top-0 z-40 mb-2 flex items-center gap-1.5 rounded-md border bg-card/95 backdrop-blur px-2 py-1.5" data-testid="matrix-hscroll">
      <button type="button" className="p-1 rounded hover:bg-muted disabled:opacity-30" disabled={atStart} onClick={() => go("start")} title="Ke awal" data-testid="matrix-scroll-start"><ChevronsLeft className="h-4 w-4" /></button>
      <button type="button" className="p-1 rounded hover:bg-muted disabled:opacity-30" disabled={atStart} onClick={() => go(-step)} title="Geser kiri" data-testid="matrix-scroll-left"><ChevronLeft className="h-4 w-4" /></button>
      <div ref={bar} onScroll={(e) => { const el = target.current; if (el && Math.abs(el.scrollLeft - e.currentTarget.scrollLeft) > 1) el.scrollLeft = e.currentTarget.scrollLeft; }} className="flex-1 overflow-x-scroll overflow-y-hidden h-4 cursor-ew-resize [scrollbar-width:thin]" data-testid="matrix-scroll-track" aria-label="Geser matriks">
        <div style={{ width: w.scroll || 1, height: 1 }} />
      </div>
      <span className={cn("num text-[10px] text-muted-foreground w-9 text-right")} data-testid="matrix-scroll-pct">{pct}%</span>
      <button type="button" className="p-1 rounded hover:bg-muted disabled:opacity-30" disabled={atEnd} onClick={() => go(step)} title="Geser kanan" data-testid="matrix-scroll-right"><ChevronRight className="h-4 w-4" /></button>
      <button type="button" className="p-1 rounded hover:bg-muted disabled:opacity-30" disabled={atEnd} onClick={() => go("end")} title="Ke akhir" data-testid="matrix-scroll-end"><ChevronsRight className="h-4 w-4" /></button>
      {onToday && <button type="button" className="ml-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold hover:bg-primary/10" onClick={onToday} data-testid="matrix-scroll-today"><CalendarDays className="h-3.5 w-3.5" /> Bulan ini</button>}
    </div>
  );
}
